import { translate, translateRich, useLocale } from "../../localization";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppButton } from "../ui";
import "./EnvironmentPerformance.scss";

/**
 * EnvironmentPerformance
 * 
 * A ranked list of environments by performance (avg score).
 * Shows clear visual bars and allows drilling down into each environment.
 * 
 * Decision it enables: "Which environments/content areas need improvement?"
 */
export default function EnvironmentPerformance({ 
  insights = [], 
  environmentData = {},
  limit = 8
}) {
  const locale = useLocale();
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState("score"); // "score" or "responses"

  const environmentStats = useMemo(() => {
    if (!insights || insights.length === 0) return [];

    // Group insights by environment
    const grouped = insights.reduce((acc, insight) => {
      const envId = insight.environmentId;
      if (!envId) return acc;

      if (!acc[envId]) {
        acc[envId] = {
          environmentId: envId,
          environmentName: environmentData[envId] || translate("Environment {value0}...", {
  value0: envId.slice(0, 8)
}),
          insights: [],
          totalResponses: 0,
          totalScore: 0,
          scoreCount: 0,
        };
      }

      acc[envId].insights.push(insight);
      acc[envId].totalResponses += insight.responseCount || 0;
      
      if (insight.meanScore != null && insight.responseCount > 0) {
        acc[envId].totalScore += (insight.meanScore || 0) * (insight.responseCount || 1);
        acc[envId].scoreCount += insight.responseCount || 1;
      }

      return acc;
    }, {});

    // Calculate averages and convert to array
    return Object.values(grouped)
      .map(env => ({
        ...env,
        avgScore: env.scoreCount > 0 ? Math.round(env.totalScore / env.scoreCount) : 0,
        questionCount: env.insights.length,
      }))
      .filter(env => env.totalResponses > 0) // Only show envs with activity
      .sort((a, b) => {
        if (sortBy === "score") return a.avgScore - b.avgScore; // Lowest first (needs attention)
        return b.totalResponses - a.totalResponses; // Most responses first
      })
      .slice(0, limit);
  }, [insights, environmentData, sortBy, limit, locale]);

  const getScoreColor = (score) => {
    if (score >= 80) return "high";
    if (score >= 60) return "mid";
    if (score >= 40) return "low";
    return "critical";
  };

  const getBarWidth = (score) => {
    return `${Math.max(score, 3)}%`; // Minimum 3% so it's visible
  };

  const handleEnvironmentClick = (env) => {
    // Navigate to first insight in this environment
    const firstInsight = env.insights[0];
    if (firstInsight) {
      const insightId = firstInsight.insightId || "";
      const q = firstInsight.textValue || "";
      navigate(`/question?insightId=${encodeURIComponent(insightId)}&q=${encodeURIComponent(q)}`);
    }
  };

  if (environmentStats.length === 0) {
    return (
      <div className="envPerformance">
        <div className="envEmpty">
          <div className="envEmptyIcon">🌍</div>
          <div className="envEmptyText">{translate("No environment data available")}</div>
          <div className="envEmptySub">{translate("Data will appear once students interact with environments")}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="envPerformance">
      {/* Header with sort toggle */}
      <div className="envHeader">
        <div className="envSortToggle">
          <span className="envSortLabel">{translate("Sort by:")}</span>
          <AppButton
            type="button"
            kind={sortBy === "score" ? "primary" : "secondary"}
            className={`envSortBtn ${sortBy === "score" ? "active" : ""}`}
            onClick={() => setSortBy("score")}
          >{translate("Lowest Score")}</AppButton>
          <AppButton
            type="button"
            kind={sortBy === "responses" ? "primary" : "secondary"}
            className={`envSortBtn ${sortBy === "responses" ? "active" : ""}`}
            onClick={() => setSortBy("responses")}
          >{translate("Most Active")}</AppButton>
        </div>
      </div>

      {/* Environment list */}
      <div className="envList">
        {environmentStats.map((env, idx) => {
          const scoreColor = getScoreColor(env.avgScore);

          return (
            <div 
              key={env.environmentId}
              className="envItem"
              onClick={() => handleEnvironmentClick(env)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && handleEnvironmentClick(env)}
            >
              {/* Rank number */}
              <div className="envRank">
                {idx + 1}
              </div>

              {/* Main content */}
              <div className="envContent">
                {/* Name and stats row */}
                <div className="envNameRow">
                  <div className="envName" title={env.environmentName}>
                    {env.environmentName}
                  </div>
                  <div className="envStats">
                    <span className="envQuestions">{translateRich("{value0} question{value1}", {
  value0: env.questionCount,
  value1: env.questionCount !== 1 ? 's' : ''
})}</span>
                    <span className="envDivider">·</span>
                    <span className="envResponses">{translateRich("Responses: {value0}", {value0: env.totalResponses})}</span>
                  </div>
                </div>

                {/* Score bar */}
                <div className="envBarContainer">
                  <div className="envBarTrack">
                    <div 
                      className={`envBar envBar--${scoreColor}`}
                      style={{ width: getBarWidth(env.avgScore) }}
                    />
                  </div>
                  <div className={`envScore envScore--${scoreColor}`}>
                    {env.avgScore}%
                  </div>
                </div>
              </div>

              {/* Arrow */}
              <div className="envArrow">→</div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="envLegend">
        <span className="envLegendLabel">{translate("Performance:")}</span>
        <span className="envLegendItem">
          <span className="envLegendBox envLegendBox--critical" /> &lt;40%
        </span>
        <span className="envLegendItem">
          <span className="envLegendBox envLegendBox--low" /> 40-60%
        </span>
        <span className="envLegendItem">
          <span className="envLegendBox envLegendBox--mid" /> 60-80%
        </span>
        <span className="envLegendItem">
          <span className="envLegendBox envLegendBox--high" /> 80%+
        </span>
      </div>

      {/* Footer */}
      <div className="envFooter">{translate("Click any environment to explore its questions")}</div>
    </div>
  );
}
